/**
 * @module i18n/helpers
 * @description Core helper functions for the custom i18n system.
 * Provides deep merging of shared + locale-specific translations,
 * dot-notation key resolution, and parameter substitution.
 */

/** Supported locale codes */
export type LocaleCode = "de-DE" | "en-US";

/** Default locale used as fallback */
export const DEFAULT_LOCALE: LocaleCode = "de-DE";

/** All supported locale codes */
export const SUPPORTED_LOCALES: LocaleCode[] = ["de-DE", "en-US"];

/**
 * Deep-merges two objects. Values from `override` take precedence.
 * Used to merge shared.json with locale-specific JSON.
 *
 * @param base - The base object (shared translations)
 * @param override - The override object (locale-specific translations)
 * @returns Merged object
 */
export function deepMerge(
  base: Record<string, any>,
  override: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = { ...base };

  for (const key of Object.keys(override)) {
    if (
      key in result &&
      typeof result[key] === "object" &&
      result[key] !== null &&
      !Array.isArray(result[key]) &&
      typeof override[key] === "object" &&
      override[key] !== null &&
      !Array.isArray(override[key])
    ) {
      result[key] = deepMerge(result[key], override[key]);
    } else {
      result[key] = override[key];
    }
  }

  return result;
}

/**
 * Resolves a dot-notation key path against a nested object.
 *
 * @param dict - The merged translation dictionary
 * @param key - Dot-separated key path (e.g. "cv.about.title")
 * @param params - Optional parameter substitution (replaces `{key}` patterns)
 * @returns The resolved string, or the raw key if not found
 *
 * @example
 * ```ts
 * resolveKey(dict, "cv.about.title");
 * // → "Staatlich geprüfter IT-Assistent | Softwareentwickler"
 *
 * resolveKey(dict, "projects.projectCount", { count: "14" });
 * // → "14 Projekte"
 * ```
 */
export function resolveKey(
  dict: Record<string, any>,
  key: string,
  params?: Record<string, any>,
): string {
  const normalizedKey = key.replace(
    /^([A-Z][a-z]+)[._]/,
    (match, p1) => `${p1.toLowerCase()}.`,
  );
  const parts = normalizedKey.split(".");
  let current: any = dict;

  for (const part of parts) {
    if (current !== null && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return key; // Key not found → return raw key as fallback
    }
  }

  if (current === undefined || current === null) return key;

  // If we landed on an object, return the key (it's a namespace, not a leaf value)
  if (typeof current === "object" && !Array.isArray(current)) return key;

  let str = String(current);

  // Parameter substitution: replace {paramName} with param values
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replaceAll(`{${k}}`, String(v));
    }
  }

  return str;
}

/**
 * Resolves a key to an array of strings.
 * If the value is a JSON array, returns it directly.
 * If it's a comma-separated string, splits it.
 *
 * @param dict - The merged translation dictionary
 * @param key - Dot-separated key path
 * @param params - Optional parameter substitution
 * @returns Array of resolved strings
 */
export function resolveKeyArr(
  dict: Record<string, any>,
  key: string,
  params?: Record<string, any>,
): string[] {
  const normalizedKey = key.replace(
    /^([A-Z][a-z]+)[._]/,
    (match, p1) => `${p1.toLowerCase()}.`,
  );
  const parts = normalizedKey.split(".");
  let current: any = dict;

  for (const part of parts) {
    if (current !== null && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return [];
    }
  }

  if (current === undefined || current === null) return [];

  if (Array.isArray(current)) {
    return current.map((v) => {
      let str = String(v);
      if (params) {
        for (const [k, val] of Object.entries(params)) {
          str = str.replaceAll(`{${k}}`, String(val));
        }
      }
      return str;
    });
  }

  const str = String(current);
  if (str.includes(",")) {
    return str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [str];
}
